import { usePaginatedParentTags } from "$/util/db";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Container,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AnchorPagination from "../components/AnchorPagination";
import BaseBreadcrumbs from "../components/BaseBreadcrumbs";
import Meta from "../components/Meta";
import Section from "../components/Section";

function ParentTagSkeleton() {
  return (
    <Grid container spacing={2}>
      {[...Array(20).keys()].map((i) => (
        <Grid item xs={6} sm={4} md={3} key={i}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={140}
              sx={{ borderTopLeftRadius: 2, borderTopRightRadius: 2 }}
            />
            <CardContent>
              <Typography variant="h5" component="div">
                <Skeleton width="80%" height={32} />
              </Typography>
              <Typography variant="body2">
                <Skeleton width="100%" height={20} />
                <Skeleton width="60%" height={20} />
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

function ParentTagsPage() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Parse page from URL query parameter or default to 1
  const getPageFromQuery = React.useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get("page");
    return pageParam ? parseInt(pageParam, 10) : 1;
  }, [location.search]);

  const [currentPage, setCurrentPage] = React.useState(() => getPageFromQuery());
  const itemsPerPage = 100;

  // Use paginated parent tags hook
  const { parentTags, isLoading, totalCount, totalPages } = usePaginatedParentTags(
    currentPage,
    itemsPerPage,
  );

  const count = totalCount ? totalCount.toLocaleString() : "0";
  const title = "Tattoo Categories";

  // Update page when URL query parameter changes
  React.useEffect(() => {
    const page = getPageFromQuery();
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  }, [getPageFromQuery, currentPage]);

  // Handle page change
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    if (value === 1) {
      navigate(location.pathname);
    } else {
      navigate(`${location.pathname}?page=${value}`);
    }
    window.scrollTo(0, 0);
  };

  // Use useMemo instead of useEffect to prevent infinite loops
  const itemListJSON = useMemo(() => {
    if (!parentTags || parentTags.length === 0) {
      return [];
    }

    return parentTags.map((tag, key) => ({
      "@type": "ListItem",
      position: (currentPage - 1) * itemsPerPage + key + 1,
      url: `https://blackink.ai/tattoo-categories/${tag.id}`,
    }));
  }, [parentTags, currentPage, itemsPerPage]);

  let bodyContent = null;

  if (isLoading) {
    bodyContent = (
      <>
        <Typography variant="h3" variantMapping={{ h3: "h1" }} color="primary">
          <Skeleton width={400} height={60} />
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 4 }}>
          <Skeleton width="100%" height={32} />
          <Skeleton width="80%" height={32} />
        </Typography>
        <ParentTagSkeleton />
      </>
    );
  } else {
    bodyContent = (
      <>
        <Typography variant="h3" variantMapping={{ h3: "h1" }} color="primary">
          {title}
        </Typography>
        <Typography variant="body1" sx={{ marginBottom: 4 }}>
          Browse tattoo ideas by category to find the perfect inspiration for your next
          tattoo. Explore our curated collection of tattoo categories and discover
          unique designs.
        </Typography>

        <Grid container spacing={2}>
          {parentTags && parentTags.length > 0 ? (
            parentTags.map((tag) => (
              <Grid item xs={6} sm={4} md={3} key={tag.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                  <CardActionArea
                    component={Link}
                    to={`/tattoo-categories/${tag.id}`}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                    }}
                  >
                    <CardMedia
                      component="img"
                      src={`/images/collage/${tag.id}/collage.webp`}
                      alt={`${tag.title} tattoo designs`}
                      onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                        // Fallback to a default image if collage doesn't exist
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                      }}
                      sx={{
                        height: 140,
                        objectFit: "cover",
                        borderTopLeftRadius: 2,
                        borderTopRightRadius: 2,
                      }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h5" component="div">
                        {tag.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tag.description
                          ? tag.description.substring(0, 100) +
                            (tag.description.length > 100 ? "..." : "")
                          : "Explore tattoo ideas in this category"}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary">
                No categories found.
              </Typography>
            </Grid>
          )}
        </Grid>

        {totalCount > itemsPerPage && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
            <AnchorPagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              baseHref={location.pathname}
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Meta
        title="Browse Tattoo Categories - Discover Tattoo Ideas by Category"
        description="Explore tattoo ideas organized by categories. Find the perfect tattoo inspiration by browsing through our curated collection of tattoo categories."
      />
      <Helmet>
        <script type="application/ld+json" key="json-ld-categories">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            numberOfItems: totalCount,
            mainEntityOfPage: {
              "@type": "CollectionPage",
              "@id": typeof window !== "undefined" ? window.location.href : "",
            },
            itemListElement: itemListJSON,
          })}
        </script>
      </Helmet>
      <Section>
        <Container>
          <BaseBreadcrumbs title="All Categories" />
        </Container>
        <Container>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Stack
                sx={{
                  display: { xs: "flex", md: "none" },
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
                direction="row"
              >
                <Typography variant="subtitle3" color="info.main">
                  {count} {count === "1" ? "Category" : "Categories"}
                </Typography>
              </Stack>

              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Typography variant="h6" mt={1}>
                  Browse Categories
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Discover tattoo ideas organized by themes and styles. Each category
                  contains curated collections of tattoo designs to inspire your next
                  ink.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={9}>
              {bodyContent}
            </Grid>
          </Grid>
        </Container>
      </Section>
    </>
  );
}

export default ParentTagsPage;
